import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/shared/ui/primitives'
import { relativeTime } from '@/shared/format'
import { Icon } from '@/app/icons'
import { approvalKeys, fetchTrail } from '@/features/billing/approval/api'
import { STEP_LABEL, type ApprovalTrail, type InboxEntry } from '@/features/billing/approval/types'
import { money, waitedFor } from './display'
import { CARD, CardHead, EmptyRow, LevelBars } from './ui'

/** Enough to act on this morning; the inbox page has the rest. */
const SHOWN = 5

/**
 * The approvals waiting on this person, oldest first.
 *
 * A row opens in place to show where the document is on its ladder, so
 * "who has already signed this, and who comes after me" is answered without
 * leaving the page. The decision itself still happens on the document: the
 * approve and return buttons belong next to the lines being approved.
 */
export function ApprovalQueueCard({ entries, now }: { entries: InboxEntry[]; now: Date }) {
  const [open, setOpen] = useState<number | null>(null)

  return (
    <section className={CARD} aria-label="Menunggu persetujuan Anda">
      <CardHead
        title="Menunggu persetujuan Anda"
        count={entries.length}
        link={{ to: '/billing/approvals', label: 'Lihat semua' }}
      />
      {entries.length === 0 ? (
        <EmptyRow
          title="Tidak ada yang menunggu persetujuan Anda"
          text="Dokumen baru akan muncul di sini begitu diajukan ke level Anda."
        />
      ) : (
        <ul className="divide-y divide-line">
          {entries.slice(0, SHOWN).map((entry) => (
            <QueueRow
              key={entry.id}
              entry={entry}
              now={now}
              open={open === entry.id}
              onToggle={() => setOpen((current) => (current === entry.id ? null : entry.id))}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function QueueRow({ entry, now, open, onToggle }: { entry: InboxEntry; now: Date; open: boolean; onToggle: () => void }) {
  const detailId = useId()
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={detailId}
        className={cn(
          'grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 px-5 py-3.5 text-left transition-colors hover:bg-gray-100',
          'sm:grid-cols-[minmax(0,1fr)_auto_auto_1.25rem]',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary',
        )}
      >
        <span className="min-w-0">
          <span className="block font-mono font-semibold text-gray-900">{entry.docNo}</span>
          <span className="block truncate text-gray-500">{entry.customerName}</span>
        </span>
        <span className="col-start-1 row-start-2 flex items-center gap-2 text-xs whitespace-nowrap text-gray-500 sm:col-start-auto sm:row-start-auto sm:grid sm:justify-items-end sm:gap-1">
          <LevelBars level={entry.levelNo} of={entry.totalLevels} />
          <span className={cn(entry.overdue && 'font-semibold text-warning-ink')}>
            {waitedFor(entry.waitingSince, now)} menunggu{entry.overdue ? ' · lewat batas' : ''}
          </span>
        </span>
        <span className="text-right font-semibold whitespace-nowrap text-gray-900 tabular-nums">
          {money(entry.currency, entry.totalAmt)}
          <small className="block truncate text-xs font-normal text-gray-500">
            {entry.asDelegate ? 'Sebagai delegasi' : entry.levelName}
          </small>
        </span>
        <Icon
          name="chevronDown"
          className={cn(
            'col-start-2 row-start-2 size-4 justify-self-end text-gray-400 transition-transform sm:col-start-auto sm:row-start-auto',
            open && 'rotate-180 text-primary',
          )}
        />
      </button>

      {open && (
        <div id={detailId} className="animate-rise grid gap-4 px-5 pt-1 pb-5">
          <Ladder entry={entry} />
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/billing/proforma/${entry.id}`}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-3.5 font-semibold text-white hover:bg-primary-active hover:text-white"
            >
              Buka untuk ditinjau
              <Icon name="arrowRight" className="size-4" />
            </Link>
            <span className="text-xs text-gray-500">Setujui atau kembalikan dari halaman dokumen.</span>
          </div>
        </div>
      )}
    </li>
  )
}

type NodeState = 'done' | 'now' | 'todo' | 'skip'

/**
 * The ladder, drawn from the trail the server froze at submit -- never from a
 * number of levels known here. Skipped levels stay visible, greyed, as they do
 * on the document page.
 */
function Ladder({ entry }: { entry: InboxEntry }) {
  const trail = useQuery({
    queryKey: approvalKeys.trail(entry.id),
    queryFn: () => fetchTrail(entry.id),
    staleTime: 60_000,
  })

  if (trail.isPending) return <p className="text-gray-500">Memuat jenjang persetujuan…</p>
  if (trail.isError || !trail.data[0]) {
    return <p className="text-gray-500">Jenjang persetujuan belum bisa dimuat. Buka dokumennya untuk melihat.</p>
  }

  const nodes = ladderNodes(trail.data[0], entry.levelNo)
  return (
    <div className="grid auto-cols-[minmax(8.5rem,1fr)] grid-flow-col overflow-x-auto pb-0.5">
      {nodes.map((node, index) => (
        <div key={index} className="relative pt-6 pr-2 text-[0.8rem]">
          {index < nodes.length - 1 && (
            <span
              aria-hidden="true"
              className={cn('absolute top-[7px] right-0 left-3.5 h-0.5', node.state === 'done' ? 'bg-success' : 'bg-line')}
            />
          )}
          <span
            aria-hidden="true"
            className={cn(
              'absolute top-0 left-0 grid size-4 place-items-center rounded-full border-2',
              node.state === 'done' && 'border-success bg-success text-white',
              node.state === 'now' && 'border-primary bg-card shadow-[0_0_0_4px_var(--sp-primary-light)]',
              node.state === 'todo' && 'border-gray-300 bg-card',
              node.state === 'skip' && 'border-gray-300 bg-gray-200',
            )}
          >
            {node.state === 'done' && <Icon name="check" className="size-2.5" />}
            {node.state === 'now' && <span className="size-1.5 rounded-full bg-primary" />}
          </span>
          <b className={cn('block font-semibold', node.state === 'now' ? 'text-primary' : node.state === 'skip' ? 'text-gray-400' : 'text-gray-900')}>
            {node.title}
          </b>
          <span className="block text-gray-500">{node.sub}</span>
        </div>
      ))}
    </div>
  )
}

function ladderNodes(trail: ApprovalTrail, myLevel: number): Array<{ state: NodeState; title: string; sub: string }> {
  const submit = [...trail.actions].reverse().find((action) => action.action === 'SUBMIT')
  const nodes: Array<{ state: NodeState; title: string; sub: string }> = [
    {
      state: 'done',
      title: submit?.actedByName ?? 'Pengaju',
      sub: submit ? `Pengaju · ${relativeTime(submit.actedAt)}` : 'Pengaju',
    },
  ]

  for (const step of trail.steps) {
    const approvers = trail.actions
      .filter((action) => action.levelNo === step.levelNo && action.action === 'APPROVE')
      .map((action) => action.actedByName)
      .filter(Boolean)
    const title = `Level ${step.levelNo} · ${step.levelName}`

    if (step.status === 'APPROVED') {
      nodes.push({ state: 'done', title, sub: `${approvers.join(', ') || 'Disetujui'} · ${relativeTime(step.completedAt)}` })
    } else if (step.status === 'ACTIVE') {
      nodes.push({ state: 'now', title, sub: step.levelNo === myLevel ? 'Giliran Anda' : STEP_LABEL.ACTIVE })
    } else if (step.status === 'SKIPPED' || step.status === 'CANCELLED') {
      nodes.push({ state: 'skip', title, sub: STEP_LABEL[step.status] })
    } else {
      nodes.push({ state: 'todo', title, sub: 'Berikutnya' })
    }
  }
  return nodes
}
